import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useAppStore } from '../store/appStore';
import { Loader2 } from 'lucide-react';
import { rememberReturnTo, returnToPath } from '../lib/returnTo';

export function AuthProvider({ children }) {
  const { isAuthenticated, isLoading, checkAuth, processOAuthLogin, demoLapsed } = useAuthStore();
  const { fetchSettings } = useAppStore();
  const [processingAuth, setProcessingAuth] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleAuth = async () => {
      // Back from Google, Apple or Microsoft sign-in with a one-time login code in the fragment
      const hash = window.location.hash;
      if (hash.includes('login_code=')) {
        const loginCode = hash.split('login_code=')[1]?.split('&')[0];
        if (loginCode) {
          // Clean URL first, so the code is never left in history
          window.history.replaceState(null, '', window.location.pathname);
          const result = await processOAuthLogin(loginCode);
          if (result.success) {
            // Force refresh settings for the new user
            await fetchSettings(true);
            setProcessingAuth(false);
            navigate(returnToPath());
            return;
          } else {
            setProcessingAuth(false);
            navigate('/login?error=sign_in');
            return;
          }
        }
      }

      await checkAuth();
      
      // If authenticated, load settings for regional formatting (force refresh to ensure fresh data)
      if (useAuthStore.getState().isAuthenticated) {
        await fetchSettings(true); // Force refresh to ensure we get the current user's settings
      }
      
      setProcessingAuth(false);
    };

    handleAuth();
    
    // Safety timeout - if auth processing takes more than 10 seconds, stop loading
    const timeout = setTimeout(() => {
      setProcessingAuth(false);
    }, 10000);
    
    return () => clearTimeout(timeout);
  }, [checkAuth, processOAuthLogin, navigate]);

  useEffect(() => {
    if (!processingAuth && !isLoading) {
      const publicRoutes = ['/', '/login', '/register', '/demo-ended', '/privacy', '/terms', '/refund-policy'];
      // Shared briefs and treatments are reached by token, without an account.
      // ('/treatment/' is the shared link; '/treatments/' is the signed-in editor.)
      const isPublicRoute = publicRoutes.includes(location.pathname)
        || location.pathname.startsWith('/brief/')
        || location.pathname.startsWith('/treatment/');

      if (!isAuthenticated && demoLapsed) {
        // Back after the demo's hour ran out: say so, rather than a sign-in
        // page. The flag clears once on a public page (/demo-ended is one):
        // clearing it before the navigation lands re-runs this with the old
        // path, which would send them to sign in after all.
        if (isPublicRoute) useAuthStore.setState({ demoLapsed: false });
        else navigate('/demo-ended?reason=expired');
      } else if (!isAuthenticated && !isPublicRoute) {
        // An AI agent's approval page: come back to it after signing in
        rememberReturnTo(location.pathname + location.search);
        navigate('/login');
      } else if (isAuthenticated && (location.pathname === '/login' || location.pathname === '/register')) {
        navigate(returnToPath());
      }
    }
  }, [isAuthenticated, isLoading, processingAuth, demoLapsed, location.pathname, navigate]);

  if (processingAuth || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return children;
}
